<template>
  <div>
    <v-card flat>
      <v-card-title class="secondary white--text">
        <div class="ma-0">
          <div class="d-flex align-center">
            <v-icon large left color="white">mdi-transfer</v-icon>
            <p class="subtitle-1 my-0">{{ totalItems }} {{ message }}</p>
          </div>
        </div>
        <v-spacer></v-spacer>
        <PersonSearch
          v-model="personId"
          label="Search Person"
          :hide-details="true"
          :dark="true"
          @change="handleFilterChange"
        />
      </v-card-title>

      <!-- Filters Row -->
      <v-card-text class="py-2 px-4 tertiary">
        <v-row align="center" no-gutters>
          <!-- From Date -->
          <v-col cols="12" sm="6" md="4" class="pr-2">
            <v-menu
              v-model="fromDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="fromDate"
                  label="From Date"
                  prepend-icon="mdi-calendar"
                  readonly
                  clearable
                  dense
                  outlined
                  hide-details
                  v-bind="attrs"
                  v-on="on"
                  @click:clear="handleFromDateClear"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="fromDate"
                @input="fromDateMenu = false; handleFilterChange()"
                no-title
                scrollable
              ></v-date-picker>
            </v-menu>
          </v-col>

          <!-- To Date -->
          <v-col cols="12" sm="6" md="4" class="px-2">
            <v-menu
              v-model="toDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  v-model="toDate"
                  label="To Date"
                  prepend-icon="mdi-calendar"
                  readonly
                  clearable
                  dense
                  outlined
                  hide-details
                  v-bind="attrs"
                  v-on="on"
                  @click:clear="handleToDateClear"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="toDate"
                @input="toDateMenu = false; handleFilterChange()"
                no-title
                scrollable
              ></v-date-picker>
            </v-menu>
          </v-col>

          <!-- Clear Filters Button -->
          <v-col cols="12" sm="6" md="4" class="pl-2 text-right">
            <v-btn
              v-if="hasActiveFilters"
              text
              small
              color="secondary"
              @click="clearAllFilters"
            >
              Clear Filters
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>

      <v-data-table
          :headers="headers"
          :search="search"
          :items="paymentRequestDetails"
          :server-items-length="totalItems"
          :options.sync="options"
          :loading="loading"
          class="elevation-0"
          :item-class="rowColor"
          no-data-text="No payment requests present"
          sort-by="Status"
          sort-desc
          :items-per-page="itemPerPage"
          @update:options="handleOptionsUpdate"
        >

          <template v-slot:item.PaymentType="{ item }">
            <span class="d-flex align-center">
              <v-icon v-if="item.PaymentType === 'Payment'" left>mdi-credit-card-outline</v-icon>
              <v-icon v-else left>mdi-autorenew</v-icon>
              {{ item.PaymentType }}</span>
          </template>

          <template v-slot:item.PersonName="{ item }">
            <a class="accent--text">{{ item.PersonName }}</a>
          </template>

          <template v-slot:item.Amount="{ item }">
            <span class="">{{ item.Amount | formatAmount }}</span>
          </template>

          <template v-slot:item.Status="{ item }">
            <span v-if="item.Status === 'Requested'">{{ item.Status }} <span class="ml-3 font-weight-light">{{ item.RequestedDateTime | formatDateTime }}</span> </span>
            <span v-else>{{ item.Status }}</span>
          </template>

          <template v-slot:item.actions="{ item }">
            <v-menu offset-y left>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  color="secondary"
                  dark
                  small
                  depressed
                  v-bind="attrs"
                  v-on="on"
                  class="text-capitalize"
                >
                  Actions
                  <v-icon right>mdi-chevron-down</v-icon>
                </v-btn>
              </template>
              <v-list class="secondary py-0" dark>
                <v-list-item @click="viewRequestDeatils(item)">
                  <v-list-item-title>View request details</v-list-item-title>
                  <v-list-item-icon>
                    <v-icon>mdi-file-document-outline</v-icon>
                  </v-list-item-icon>
                </v-list-item>
                <v-list-item @click="navigateToPayments(item)">
                  <v-list-item-title>View all payment requests</v-list-item-title>
                  <v-list-item-icon>
                    <v-icon>mdi-file-document-multiple-outline</v-icon>
                  </v-list-item-icon>
                </v-list-item>
                <v-list-item @click="openPayAgentApp(item)">
                  <v-list-item-title>Open in app</v-list-item-title>
                  <v-list-item-icon>
                    <v-icon>mdi-cellphone-link</v-icon>
                  </v-list-item-icon>
                </v-list-item>
                <v-list-item @click="copyAppLinkToClipboard(item)">
                  <v-list-item-title>Copy app link</v-list-item-title>
                  <v-list-item-icon>
                    <v-icon>mdi-content-copy</v-icon>
                  </v-list-item-icon>
                </v-list-item>
                <v-list-item @click="openNativeEmail(item)">
                  <v-list-item-title>Send request by email</v-list-item-title>
                  <v-list-item-icon>
                    <v-icon>mdi-email-outline</v-icon>
                  </v-list-item-icon>
                </v-list-item>
                <v-list-item @click="openCancelConfirmDialog(item)">
                  <v-list-item-title>Cancel Request</v-list-item-title>
                  <v-list-item-icon>
                    <v-icon>mdi-delete</v-icon>
                  </v-list-item-icon>
                </v-list-item>
              </v-list>
            </v-menu>
          </template>
        </v-data-table>
    </v-card>
    <v-dialog
      v-model="requestDetailsDialog"
      max-width="500px"
      transition="dialog-transition"
      persistent
    >
      <v-card>
        <v-card-title class="dialogColor white--text py-0">
          Request details
          <v-spacer></v-spacer>
          <v-btn @click="closeRequestDetailsDialog" color="white" text fab>
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pt-1">
           <RequestDetailsTable
             :item="selectedItem"
           />
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="cancelConfirmDialog"
      max-width="500px"
      transition="dialog-transition"
      persistent
    >
      <v-card>
        <v-card-title class="dialogColor white--text py-0">
          Cancel request
          <v-spacer></v-spacer>
          <v-btn @click="closeCancelConfirmDialog" color="white" text fab>
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pt-5">
          <p class="text-h6 accent--text">Do you want to cancel this payment request to:</p>
          <p class="text-h5 text-center">{{ selectedItem.PersonName }}</p>
          <RequestDetailsTable
            :item="selectedItem"
          />
          <div class="d-flex mt-8">
            <v-btn 
              @click="closeCancelConfirmDialog"
              color="red"
              outlined
              dark
            >Close</v-btn>
            <v-spacer></v-spacer>
            <v-btn 
              @click="cancelRequest"
              :loading="loading"
              color="success"
              depressed
            >Cancel request</v-btn>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <Snackbar 
      :snackbarDialog="snackbar"
      :snackbarText="snackbarText"
      :snackbarColor="snackbarColor"
      @closeSnackbar="closeSnackbar()"
      @errorCreatingRequest="showSnackbarError()"
    />
    
  </div>
</template>

<script>
import RequestDetailsTable from '@/components/PaymentRequests/RequestDetailsTable.vue';
import Snackbar from '@/components/Snackbars/TheSnackbar'
import PersonSearch from '@/components/CloudSearch/PersonSearch.vue'

export default {
  name: 'PaymentRequestsTable',
  components: {
    RequestDetailsTable,
    Snackbar,
    PersonSearch
  },
  props: {
    state: {
      type: String,
      default: 'Requested'
    },
    message: {
      type: String,
      default: 'payment requests'
    },
    isLoading: {
      type: Boolean,
      default: false
    }
  },
  watch: {
    // Watch for when the parent indicates this tab should load
    state: {
      immediate: true,
      handler() {
        this.getPaymentRequestDetails()
      }
    }
  },
  data() {
    return {
      snackbar: false,
      snackbarText: '',
      snackbarColor: '',
      loading: false,
      itemPerPage: 10,
      search: '',
      selectedItem: '',
      userDetails: '',
      paymentRequestDetails: [],
      totalItems: 0,
      options: {
        page: 1,
        itemsPerPage: 10
      },
      requestDetailsDialog: false,
      cancelConfirmDialog: false,
      headers: [
        { text: 'Type', value: 'PaymentType', sortable: false},
        { text: 'Name', value: 'PersonName', sortable: false},
        { text: 'Amount', value: 'Amount', sortable: false},
        { text: 'Staus', value: 'Status', sortable: false},
        { text: '', value: 'appLinks', align: 'end', sortable: false},
        { text: '', value: 'actions', align: 'end', sortable: false},
        // { text: 'Requested', value: 'requested'},
        // { text: 'Paid', value: 'paid'},
        // { text: 'Outstanding', value: 'outstanding'},
      ],
      // Filter data
      personId: '',
      fromDate: '',
      toDate: '',
      fromDateMenu: false,
      toDateMenu: false
    }
  },
  methods: {
    closeSnackbar() {
      this.snackbar = false
      this.snackbarText = ''
      this.snackbarColor = ''
    },
    openCancelConfirmDialog(item) {
      this.selectedItem = item
      this.cancelConfirmDialog = true
    },
    closeCancelConfirmDialog() {
      this.cancelConfirmDialog = false
      this.selectedItem = ''
    },
    cancelRequest() {
      this.loading = true
      this.$store.dispatch('PaymentRequests/cancelPaymentRequest', this.selectedItem.PaymentRedirectReference)
        .then( () => {
          this.loading = false
          this.closeCancelConfirmDialog() 
          this.$nextTick(() => this.getPaymentRequestDetails())
          this.snackbar = true
          this.snackbarText = 'Payment request cancelled.'
          this.snackbarColor = 'green'
        })
        .catch( () => {
          this.loading = false
          this.closeCancelConfirmDialog() 
          this.$nextTick(() => this.getPaymentRequestDetails())
          this.snackbar = true
          this.snackbarText = 'Error whilst cancelling request. Try again.'
          this.snackbarColor = 'red'
        })
    },
    navigateToPayments(item) {
      this.$router.push({ name: 'Payments', params: { id: item.PersonID }})
    },
    openNativeEmail(item) {
      const idRef = item.PaymentRedirectReference
      const url = `https://www.payagent.app?requestid=${idRef}`
      window.location.href = `mailto:${item.PersonEmail}?subject=${item.Breakdown[0]}&body=Hi ${item.PersonName},%0a%0aWe are contacting you regarding the following payment request: ${item.Breakdown[0]}. %0a%0aYou can take care of payments by clicking on this link: %0a%0a${url} %0a%0aPayAgent by AgentOS is quick and secure, and your security details will not be shared with us %0a%0a%0a%0aKind regards, %0a${this.$store.state.user.UserDisplayName} %0a%0a`
    },
    openPayAgentApp(item) {
      const idRef = item.PaymentRedirectReference
      const url = `https://www.payagent.app?requestid=${idRef}`
      window.open(url, '_blank')
    },
    viewRequestDeatils(item) {
      this.selectedItem = item
      this.requestDetailsDialog = true
    },
    closeRequestDetailsDialog() {
      this.selectedItem = ''
      this.requestDetailsDialog = false
    },
    handleOptionsUpdate(options) {
      if (!this.loading) {
        this.getPaymentRequestDetails(options.page, options.itemsPerPage)
      }
    },
    getPaymentRequestDetails(page = 1, itemsPerPage = 10) {
      this.loading = true
      
      const offset = (page - 1) * itemsPerPage
      
      // Determine which action to dispatch based on state
      let action = 'getRequestedPayments'
      const stateType = this.state.toLowerCase()
      
      if (stateType === 'partiallypaid') {
        action = 'getPartiallyPaidPayments'
      } else if (stateType === 'fullypaid') {
        action = 'getFullyPaidPayments'
      } else if (stateType === 'cancelled') {
        action = 'getCancelledPayments'
      } else if (stateType === 'stosetup') {
        action = 'getStoSetupPayments'
      } else if (stateType === '') {
        action = 'getAllPayments'
      }

      // Build parameters object with filters
      const params = {
        offset: offset,
        count: itemsPerPage
      }
      
      // Add filter parameters if they have values
      if (this.personId) params.personId = this.personId
      if (this.fromDate) params.fromDate = this.fromDate
      if (this.toDate) params.toDate = this.toDate

      this.$store.dispatch(`PaymentRequests/${action}`, params)
        .then(res => {
          this.paymentRequestDetails = res.data || []
          this.totalItems = res.count || 0
        })
        .catch(err => {
          console.error('Error fetching payment requests:', err)
          this.paymentRequestDetails = []
          this.totalItems = 0
        })
        .finally(() => {
          this.loading = false
        })
    },
    handleFilterChange() {
      // Reset to first page when filters change
      this.options.page = 1
      this.getPaymentRequestDetails(1, this.options.itemsPerPage)
    },
    handleFromDateClear() {
      this.fromDate = ''
      this.handleFilterChange()
    },
    handleToDateClear() {
      this.toDate = ''
      this.handleFilterChange()
    },
    clearAllFilters() {
      this.personId = ''
      this.fromDate = ''
      this.toDate = ''
      this.handleFilterChange()
    },
    rowColor(item) {
      if(item.Status === 'FullyPaid') return 'green lighten-5'
      if(item.Status === 'Cancelled') return 'red lighten-5'
      return ''
    },
    copyAppLinkToClipboard(item) {
        const idRef = item.PaymentRedirectReference
        const str = `https://www.payagent.app?requestid=${idRef}`
        // Create new element
        var el = document.createElement('textarea');
        // Set value (string to be copied)
        el.value = str;
        // Set non-editable to avoid focus and move outside of view
        el.setAttribute('readonly', '');
        el.style = {position: 'absolute', left: '-9999px'};
        document.body.appendChild(el);
        // Select text inside element
        el.select();
        // Copy text to clipboard
        document.execCommand('copy');
        // Remove temporary element
        document.body.removeChild(el);
      },
  },
  computed: {
    iconType() {
      if(this.userDetails.PersonType === 'Tenant') return 'mdi-home-account'
      if(this.userDetails.PersonType === 'Landlord') return 'mdi-account-tie'
      if(this.userDetails.PersonType === 'Contractor') return 'mdi-tools'
      return 'mdi-account'
    },
    hasActiveFilters() {
      return this.personId || this.fromDate || this.toDate
    }
  }
}
</script>

